<template>
  <div class="container-historical">
    <HeadingDisplay :title="props.historical.label" size="large" />
    <PerformanceHeader
      class="performance-header"
      :headers="headers"
      :is-loading="false"
    />
    <Grid class="container-grid" :columns="[1, 1]" :gutter="24">
      <div data-testid="performance-historical">
        <template v-for="(item, index) in props.historical.monthlies">
          <Row
            v-if="index < 6"
            :key="index"
            class="row-item"
            chevron
            :has-interaction="!item.values.isEmpty"
            :has-line="index > 0"
            :left-title="item.label"
            @click.stop="handleClick(item)"
          >
            <template
              v-if="item.values?.benchmarkComparison?.formatted"
              #bottom-left
            >
              <BaseText
                size="sm"
                as="p"
                :color="
                  !item.values?.benchmarkComparison?.isNegative
                    ? NebraskaColors.monetaryPositive
                    : NebraskaColors.monetaryNegative
                "
              >
                <Money>
                  {{
                    getBenchmarkComparisonValue(
                      item.values?.benchmarkComparison?.formatted
                    )
                  }}
                </Money>
              </BaseText>
            </template>
            <template #bottom-right>
              <Money>
                {{ item.values?.returns?.formatted }}
              </Money>
            </template>
            <template #top-right>
              <Money>
                {{ item.values?.profitValues?.formatted }}
              </Money>
            </template>
          </Row>
        </template>
      </div>
      <div>
        <template v-for="(item, index) in props.historical.monthlies">
          <Row
            v-if="index > 5"
            :key="index"
            class="row-item"
            chevron
            :has-interaction="!item.values.isEmpty"
            :has-line="index > 6"
            :left-title="item.label"
            @click.stop="handleClick(item)"
          >
            <template
              v-if="item.values?.benchmarkComparison?.formatted"
              #bottom-left
            >
              <BaseText
                size="sm"
                as="p"
                :color="
                  !item.values?.benchmarkComparison?.isNegative
                    ? NebraskaColors.monetaryPositive
                    : NebraskaColors.monetaryNegative
                "
              >
                <Money>
                  {{
                    getBenchmarkComparisonValue(
                      item.values?.benchmarkComparison?.formatted
                    )
                  }}
                </Money>
              </BaseText>
            </template>
            <template #bottom-right>
              <Money>
                {{ item.values?.returns?.formatted ?? '' }}
              </Money>
            </template>
            <template #top-right>
              <Money>
                {{ item.values?.profitValues?.formatted ?? '' }}
              </Money>
            </template>
          </Row>
        </template>
      </div>
    </Grid>
  </div>
</template>
<script lang="ts" setup>
import {
  IMonthly,
  IPerformanceYear
} from '@/modules/investments/types/Performance';
import PerformanceHeader from '@/modules/investments/views/Performance/components/PerformanceHeader/PerformanceHeader.vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  HeadingDisplay,
  Grid,
  Row,
  StatusColors,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';
import { computed } from 'vue';

interface Props {
  historical: IPerformanceYear;
  benchmarkSelected: string;
  benchmarkComparisonSelected: string;
}

const props = defineProps<Props>();

interface Emits {
  (event: 'show-details', value: IMonthly): void;
}

const emit = defineEmits<Emits>();

const headers = computed(() => [
  {
    description: 'Rendimento bruto',
    value: props.historical?.values?.profitValues?.formatted
  },
  {
    description: 'Rentabilidade bruta',
    value: props.historical?.values?.returns?.formatted
  },
  {
    description: props.benchmarkSelected ?? '',
    value: props.historical?.values?.benchmark?.formatted ?? ''
  },
  {
    description: props.benchmarkComparisonSelected ?? '',
    value: props.historical?.values?.benchmarkComparison?.formatted ?? '',
    statusValue: !props.historical?.values?.benchmarkComparison?.isNegative
      ? StatusColors.POSITIVE
      : StatusColors.NEGATIVE
  }
]);

const getBenchmarkComparisonValue = (value: string) => {
  if (!props?.benchmarkSelected) return '';
  return props?.benchmarkSelected === 'CDI'
    ? `${value} do CDI`
    : `${props?.benchmarkComparisonSelected} ${value}`;
};

const handleClick = (value: IMonthly) => {
  if (!value?.values?.isEmpty) {
    emit('show-details', value);
  }
};
</script>
<style scoped lang="less">
.container-historical {
  width: 100%;
  .performance-header {
    margin: @size-spacing-x500 0;
  }

  .container-grid {
    color: @divider-primary;
    border-top: 1px solid @divider-primary;

    :deep(.row-item) {
      > div {
        padding: @size-spacing-x450 0;
      }
      .wrapper {
        align-items: center;
      }
    }

    .row-item :deep(.container-left) {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    :deep(.row-item:not(.row-item__interaction)) {
      .subtitle,
      .container-left,
      .container-right,
      .icon svg path,
      .money-text {
        pointer-events: none;
        color: @element-disabled;
        fill: @element-disabled;
      }
    }
  }
}
</style>
